const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  storageKey: string;
  mediaId: string;
  downloadUrl: string;
}

interface UploadUrlResponse {
  uploadUrl: string;
  storageKey: string;
}

interface MediaResponse {
  id: string;
  mediaType: string;
  storageKey: string;
  originalFilename: string;
  contentType: string;
  fileSize: number;
  downloadUrl: string;
  createdAt: string;
}

const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Thrown when the browser can't read the picked file. On Android, files picked from a content
 * provider (Google Drive, WhatsApp, "Recent", some downloads) can fail to open when Chromium
 * reads the body; XHR then fails before the PUT leaves the device and looks like a network
 * error. The message already names the file, so callers shouldn't prefix it again.
 */
export class FileReadError extends Error {
  constructor(fileName: string) {
    super(
      `Couldn't read "${fileName}" from your device. Save the file to your phone's storage ` +
        '(for example Downloads) and choose it again, or upload a photo instead.'
    );
    this.name = 'FileReadError';
  }
}

/**
 * Read the whole file into memory and return it as a Blob with the same type. Uploads are
 * capped at 10 MB, so this is safe, and it surfaces an unreadable file as a clear error
 * instead of a status-0 XHR failure.
 */
export async function readFileBody(file: File): Promise<Blob> {
  try {
    const bytes = await file.arrayBuffer();
    return new Blob([bytes], { type: file.type });
  } catch {
    throw new FileReadError(file.name);
  }
}

/**
 * PUT a body to an S3 presigned URL (XHR for progress). `contentType` must be the exact value
 * the URL was signed with.
 */
export function putToPresignedUrl(
  uploadUrl: string,
  body: Blob,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percentage: Math.round((e.loaded / e.total) * 100),
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () =>
      reject(new Error("The upload couldn't reach our storage. Check your connection and try again."));
    xhr.ontimeout = () =>
      reject(new Error('The upload timed out. Check your connection and try again.'));
    xhr.send(body);
  });
}

/**
 * Upload a file using presigned URLs (3-step flow):
 * 1. Get presigned URL from backend
 * 2. PUT file directly to S3
 * 3. Confirm upload with backend
 */
export async function uploadFile(
  file: File,
  mediaType: string,
  token: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Step 0: read the file first, so we never mint an upload URL we can't use.
  const body = await readFileBody(file);

  // Step 1: Get presigned upload URL
  const uploadUrlRes = await fetch(`${API_BASE_URL}/api/garages/me/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      mediaType,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlRes.ok) {
    const err = await uploadUrlRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, storageKey }: UploadUrlResponse = await uploadUrlRes.json();

  // Step 2: PUT file directly to S3 via presigned URL (using XHR for progress)
  await putToPresignedUrl(uploadUrl, body, file.type, onProgress);

  // Step 3: Confirm upload with backend
  const confirmRes = await fetch(`${API_BASE_URL}/api/garages/me/confirm-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      storageKey,
      mediaType,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
    }),
  });

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to confirm upload');
  }

  const media: MediaResponse = await confirmRes.json().catch(() => ({} as MediaResponse));
  return {
    // PROMOTION_BANNER confirm stores the key on the offer, not in garage_media, so the
    // response may not echo a media row — fall back to the key we uploaded to.
    storageKey: media.storageKey ?? storageKey,
    mediaId: media.id,
    downloadUrl: media.downloadUrl,
  };
}

/**
 * Upload a document file (TRADE_LICENSE, OWNER_EMIRATES_ID, OTHER) using presigned URLs (3-step):
 * 1. Get presigned URL from backend
 * 2. PUT file directly to S3
 * 3. Submit a GarageDocument row via POST /api/garages/me/documents
 *
 * Distinct from {@link uploadFile}: that one creates a GarageMedia row + sets cache columns;
 * this one creates a GarageDocument row in garage_documents (used by admin docs tab + approval).
 */
export async function uploadDocument(
  file: File,
  documentType: 'TRADE_LICENSE' | 'OWNER_EMIRATES_ID' | 'OTHER',
  token: string,
  options: { licenseNumber?: string } = {},
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Step 0: read the file first, so we never mint an upload URL we can't use.
  const body = await readFileBody(file);

  // Step 1: presigned URL — the upload-url endpoint validates `mediaType` against the
  // `MediaType` enum (GarageMedia), which doesn't include OWNER_EMIRATES_ID. Map document
  // types to the matching MediaType: TRADE_LICENSE has its own enum value; everything else
  // falls back to OTHER_DOCUMENT. The storage key path is the only consumer of this field —
  // we never create a media row because we skip confirm-upload and go straight to the
  // documents endpoint instead.
  const uploadMediaType = documentType === 'TRADE_LICENSE' ? 'TRADE_LICENSE' : 'OTHER_DOCUMENT';
  const uploadUrlRes = await fetch(`${API_BASE_URL}/api/garages/me/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      mediaType: uploadMediaType,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlRes.ok) {
    const err = await uploadUrlRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, storageKey }: UploadUrlResponse = await uploadUrlRes.json();

  // Step 2: PUT to S3
  await putToPresignedUrl(uploadUrl, body, file.type, onProgress);

  // Step 3: submit a GarageDocument row
  const submitRes = await fetch(`${API_BASE_URL}/api/garages/me/documents`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      documentType,
      storageKey,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
      ...(documentType === 'TRADE_LICENSE' && options.licenseNumber
        ? { licenseNumber: options.licenseNumber }
        : {}),
    }),
  });

  if (!submitRes.ok) {
    const err = await submitRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to submit document');
  }

  const doc = await submitRes.json();
  return {
    storageKey: doc.storageKey,
    mediaId: doc.id,
    downloadUrl: doc.downloadUrl || '',
  };
}

/**
 * Stage a file upload for change requests (2-step flow — NO confirm-upload):
 * 1. Get presigned URL from backend
 * 2. PUT file directly to S3
 * Does NOT create a GarageMedia DB record. The record is created only
 * when the admin approves the change request.
 */
export async function stageUpload(
  file: File,
  mediaType: string,
  token: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Step 0: read the file first, so we never mint an upload URL we can't use.
  const body = await readFileBody(file);

  // Step 1: Get presigned upload URL
  const uploadUrlRes = await fetch(`${API_BASE_URL}/api/garages/me/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      mediaType,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlRes.ok) {
    const err = await uploadUrlRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, storageKey }: UploadUrlResponse = await uploadUrlRes.json();

  // Step 2: PUT file directly to S3 via presigned URL (using XHR for progress)
  await putToPresignedUrl(uploadUrl, body, file.type, onProgress);

  // Step 3: Get download URL for preview (file is in S3, no DB record needed)
  const downloadRes = await fetch(
    `${API_BASE_URL}/api/garages/me/download-url?storageKey=${encodeURIComponent(storageKey)}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );

  let downloadUrl = '';
  if (downloadRes.ok) {
    const result = await downloadRes.json();
    downloadUrl = result.downloadUrl || result.url || '';
  }

  return {
    storageKey,
    mediaId: `staged-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    downloadUrl,
  };
}

/**
 * Upload a service offering media file using presigned URLs (3-step flow):
 * 1. Get presigned URL from backend (service-level endpoint)
 * 2. PUT file directly to S3
 * 3. Confirm upload with backend
 */
export async function uploadServiceMedia(
  offeringId: string,
  file: File,
  mediaType: string,
  token: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Step 0: read the file first, so we never mint an upload URL we can't use.
  const body = await readFileBody(file);

  // Step 1: Get presigned upload URL
  const uploadUrlRes = await fetch(`${API_BASE_URL}/api/garages/me/services/${offeringId}/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      mediaType,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlRes.ok) {
    const err = await uploadUrlRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, storageKey }: UploadUrlResponse = await uploadUrlRes.json();

  // Step 2: PUT file directly to S3 via presigned URL (using XHR for progress)
  await putToPresignedUrl(uploadUrl, body, file.type, onProgress);

  // Step 3: Confirm upload with backend
  const confirmRes = await fetch(`${API_BASE_URL}/api/garages/me/services/${offeringId}/confirm-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      storageKey,
      mediaType,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
    }),
  });

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to confirm upload');
  }

  // The service-offering confirm endpoint returns ServiceOfferingMediaResponse, whose id
  // field is `mediaId` — reading only `id` left mediaId undefined on every service upload.
  // Nothing consumed it until the gallery reorder needed the ids of freshly uploaded photos.
  const media: MediaResponse & { mediaId?: string } = await confirmRes.json();
  return {
    storageKey: media.storageKey,
    mediaId: media.mediaId ?? media.id,
    downloadUrl: media.downloadUrl,
  };
}

/**
 * Upload a service proposal media file using presigned URLs (3-step flow).
 */
export async function uploadProposalMedia(
  proposalId: string,
  file: File,
  mediaType: string,
  token: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Step 0: read the file first, so we never mint an upload URL we can't use.
  const body = await readFileBody(file);

  // Step 1: Get presigned upload URL
  const uploadUrlRes = await fetch(`${API_BASE_URL}/api/garages/me/service-proposals/${proposalId}/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      mediaType,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlRes.ok) {
    const err = await uploadUrlRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }

  const { uploadUrl, storageKey }: UploadUrlResponse = await uploadUrlRes.json();

  // Step 2: PUT file directly to S3 via presigned URL
  await putToPresignedUrl(uploadUrl, body, file.type, onProgress);

  // Step 3: Confirm upload with backend
  const confirmRes = await fetch(`${API_BASE_URL}/api/garages/me/service-proposals/${proposalId}/confirm-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      storageKey,
      mediaType,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
    }),
  });

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to confirm upload');
  }

  const media = await confirmRes.json();
  return {
    storageKey: media.storageKey,
    mediaId: media.mediaId || media.id,
    downloadUrl: media.downloadUrl,
  };
}

/**
 * Delete a service proposal media file.
 */
export async function deleteProposalMedia(
  proposalId: string,
  mediaId: string,
  token: string
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/garages/me/service-proposals/${proposalId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete media');
  }
}

export interface ServiceOfferingMedia {
  mediaId: string;
  mediaType: string;
  storageKey: string;
  originalFilename: string | null;
  contentType: string | null;
  fileSize: number | null;
  downloadUrl: string;
  sortOrder: number | null;
}

/**
 * List a service offering's live media, with presigned download URLs.
 * The offering response only carries bare URLs, so this is the only way the UI
 * learns each photo's mediaId — which it needs to delete or replace one.
 */
export async function listServiceMedia(
  offeringId: string,
  token: string
): Promise<ServiceOfferingMedia[]> {
  const res = await fetch(`${API_BASE_URL}/api/garages/me/services/${offeringId}/media`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to load service media');
  }
  return res.json();
}

/**
 * Delete a service offering media file.
 */
export async function deleteServiceMedia(
  offeringId: string,
  mediaId: string,
  token: string
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/garages/me/services/${offeringId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete media');
  }
}

/**
 * Validate file before upload (client-side pre-check)
 */
export function validateFile(file: File, mediaType: string): string | null {
  const imageTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const docTypes = ['application/pdf', 'image/jpeg', 'image/png'];

  const maxSizes: Record<string, number> = {
    TRADE_LICENSE: 10 * 1024 * 1024,
    OWNER_EMIRATES_ID: 5 * 1024 * 1024,
    LOGO: 5 * 1024 * 1024,
    THUMBNAIL: 5 * 1024 * 1024,
    GALLERY_PHOTO: 5 * 1024 * 1024,
    SERVICE_IMAGE: 5 * 1024 * 1024,
    SERVICE_THUMBNAIL: 2 * 1024 * 1024,
    SERVICE_PHOTO: 5 * 1024 * 1024,
    PROPOSAL_PHOTO: 5 * 1024 * 1024,
    PROMOTION_BANNER: 5 * 1024 * 1024,
  };

  const allowedTypes: Record<string, string[]> = {
    TRADE_LICENSE: docTypes,
    OWNER_EMIRATES_ID: docTypes,
    LOGO: imageTypes,
    THUMBNAIL: imageTypes,
    GALLERY_PHOTO: imageTypes,
    SERVICE_IMAGE: imageTypes,
    SERVICE_THUMBNAIL: imageTypes,
    SERVICE_PHOTO: imageTypes,
    PROPOSAL_PHOTO: imageTypes,
    PROMOTION_BANNER: imageTypes,
  };

  const allowed = allowedTypes[mediaType];
  if (allowed && !allowed.includes(file.type)) {
    return `File type "${file.type}" is not allowed. Accepted: ${allowed.join(', ')}`;
  }

  const maxSize = maxSizes[mediaType];
  if (maxSize && file.size > maxSize) {
    return `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum ${(maxSize / 1024 / 1024).toFixed(0)}MB`;
  }

  return null;
}
